import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, ArrowRight, Check, Mail, Users, CalendarDays } from "lucide-react";
import { track } from "../lib/analytics";
import CountdownReminder from "./CountdownReminder";
import { IMAGES } from "../data/content";
import {
  FORM_INTEGRATION_MODE,
  FLUENT_FORM_ID,
  PRIVATE_GROUP_URL,
  THANK_YOU_PAGE_URL,
} from "../config";

// URL de l'API : configurable au runtime (window.TRAVERSEE_API_URL) sans reconstruire,
// sinon on utilise l'URL gravée au build (REACT_APP_BACKEND_URL).
const API = `${
  (typeof window !== "undefined" && window.TRAVERSEE_API_URL) ||
  process.env.REACT_APP_BACKEND_URL
}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// --- Envoi DIRECT dans Fluent Forms (même origine que le site WordPress) ---
// Indépendant de tout backend externe : sur le site de Gilles, la requête est
// same-origin vers /wp-admin/admin-ajax.php → aucune dépendance, aucun CORS.
const FF_AJAX_URL =
  (typeof window !== "undefined" && window.TRAVERSEE_FF_AJAX) ||
  (typeof window !== "undefined"
    ? `${window.location.origin}/wp-admin/admin-ajax.php`
    : "");
const FF_FORM_ID = "60";
const FF_EMBED_POST_ID = "54061";
const FF_NONCE = "b69e74ad5a";

async function submitToFluentForm({ prenom, email }) {
  const inner = new URLSearchParams();
  inner.append("names[first_name]", prenom);
  inner.append("names[last_name]", "");
  inner.append("email", email);
  inner.append(`fluentform_${FF_FORM_ID}_fluentformnonce`, FF_NONCE);
  inner.append("__fluent_form_embded_post_id", FF_EMBED_POST_ID);
  inner.append("_wp_http_referer", (typeof window !== "undefined" && window.location.pathname) || "/");
  const body = new URLSearchParams();
  body.append("action", "fluentform_submit");
  body.append("form_id", FF_FORM_ID);
  body.append("data", inner.toString());
  const res = await fetch(FF_AJAX_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      "X-Requested-With": "XMLHttpRequest",
    },
    credentials: "same-origin",
    body: body.toString(),
  });
  const json = await res.json(); // lève une exception si ce n'est pas du JSON (ex: aperçu)
  return !!(json && json.success);
}

const getAttribution = (formLocation) => {
  const p = new URLSearchParams(window.location.search);
  const val = (k) => p.get(k) || null;
  let timezone = null;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (e) {
    timezone = null;
  }
  return {
    utm_source: val("utm_source"),
    utm_medium: val("utm_medium"),
    utm_campaign: val("utm_campaign"),
    utm_content: val("utm_content"),
    utm_term: val("utm_term"),
    landing_page_url: window.location.href,
    referrer_url: document.referrer || null,
    form_location: formLocation,
    language: document.documentElement.lang || navigator.language || "fr",
    timezone,
  };
};

// Bloc de confirmation après inscription réussie
const Confirmation = ({ testid }) => (
  <div className="lead-confirm" data-testid={`${testid}-confirmation`}>
    <div className="lead-confirm__media">
      <img src={IMAGES.dolphins} alt="Dauphins sauvages nageant en pleine mer" loading="eager" />
      <div className="lead-confirm__badge">
        <Check size={24} strokeWidth={1.6} />
      </div>
    </div>

    <h3 className="font-display lead-confirm__title">Bienvenue dans La Traversée.</h3>
    <p className="lead-confirm__lead">Ton premier pas est fait.</p>
    <p className="lead-confirm__text">
      Tu vas recevoir un email contenant toutes les informations nécessaires. Pense à
      vérifier l'onglet Promotions ou tes courriers indésirables si tu ne le vois pas
      arriver.
    </p>

    <ul className="lead-confirm__steps">
      <li>
        <span className="lead-confirm__step-icon"><Mail size={16} strokeWidth={1.7} /></span>
        <span>Ouvre l'email de confirmation et garde-le précieusement.</span>
      </li>
      <li>
        <span className="lead-confirm__step-icon"><Users size={16} strokeWidth={1.7} /></span>
        <span>Rejoins le groupe privé pour rester relié au collectif.</span>
      </li>
      <li>
        <span className="lead-confirm__step-icon"><CalendarDays size={16} strokeWidth={1.7} /></span>
        <span>Réserve tes trois soirées, du 23 au 30 septembre 2026.</span>
      </li>
    </ul>

    <a
      href={PRIVATE_GROUP_URL || "#"}
      target={PRIVATE_GROUP_URL ? "_blank" : undefined}
      rel="noopener noreferrer"
      className="btn btn--primary btn--full"
      data-testid={`${testid}-private-group-btn`}
      onClick={() => track("private_group_click", { location: testid })}
    >
      REJOINDRE LE GROUPE PRIVÉ
      <ArrowRight size={18} strokeWidth={1.8} />
    </a>
    <p className="lead-confirm__hint">Groupe privé Facebook · réservé aux participants</p>
  </div>
);

// Emplacement de remplacement Fluent Forms (mode 'fluentforms')
const FluentFormsSlot = ({ variant }) => (
  <div className={`traversee-${variant}-form ff-slot`} data-testid={`${variant}-fluentforms-slot`}>
    {/*
      MODE B — FLUENT FORMS (WordPress)
      Ce conteneur est prêt à accueillir le shortcode fourni :
      [fluentform id="60"]
      Sur WordPress, coller ce shortcode ici. Les styles .ff-el-input--content input
      et .ff-btn-submit sont déjà prêts (App.css) pour conserver le design.
      NB : un shortcode WordPress ne s'exécute pas dans un aperçu React pur ;
      d'où l'affichage du formulaire natif fonctionnel tant que la page n'est pas
      intégrée à WordPress.
    */}
    <p className="ff-slot__note">
      Emplacement Fluent Forms — shortcode : <code>[fluentform id="{FLUENT_FORM_ID}"]</code>
    </p>
  </div>
);

export default function LeadForm({ variant = "hero", formLocation = "hero" }) {
  const testid = `${variant}-form`;
  const [prenom, setPrenom] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus] = useState("idle"); // idle|loading|success|duplicate|error
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [started, setStarted] = useState(false);

  const prenomRef = useRef(null);
  const emailRef = useRef(null);
  const consentRef = useRef(null);
  const rootRef = useRef(null);

  // Événement de vue du formulaire
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            track(variant === "hero" ? "hero_form_view" : "final_form_view", {
              location: formLocation,
            });
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [variant, formLocation]);

  const markStarted = () => {
    if (!started) {
      setStarted(true);
      track(variant === "hero" ? "hero_form_start" : "final_form_start", {
        location: formLocation,
      });
    }
  };

  const focusFirstError = (errs) => {
    if (errs.prenom && prenomRef.current) prenomRef.current.focus();
    else if (errs.email && emailRef.current) emailRef.current.focus();
    else if (errs.consent && consentRef.current) consentRef.current.focus();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return; // anti double-clic

    // Validation front-end
    const errs = {};
    if (!prenom.trim())
      errs.prenom = "Indique simplement ton prénom pour poursuivre.";
    if (!EMAIL_RE.test(email.trim()))
      errs.email = "Cette adresse semble incomplète. Vérifie-la avant de continuer.";
    if (!consent)
      errs.consent = "Ton accord est nécessaire pour recevoir les accès à La Traversée.";

    setErrors(errs);
    setGlobalError("");
    if (Object.keys(errs).length) {
      focusFirstError(errs);
      track("form_error", { location: formLocation, reason: "validation" });
      return;
    }

    setStatus("loading");
    track(variant === "hero" ? "hero_form_submit" : "final_form_submit", {
      location: formLocation,
    });

    const cleanPrenom = prenom.trim();
    const cleanEmail = email.trim();
    const attribution = getAttribution(formLocation);

    const finishSuccess = (via) => {
      setStatus("success");
      track("form_success", { location: formLocation, via });
      // Conversion Meta (Pixel) + Google : envoyée UNIQUEMENT sur inscription réelle réussie.
      try {
        if (typeof window.fbq === "function") {
          window.fbq("track", "Lead", { content_name: "La Traversée" });
          window.fbq("track", "CompleteRegistration", { content_name: "La Traversée", status: true });
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "traversee_inscription", form_location: formLocation, via });
        if (typeof window.gtag === "function") {
          window.gtag("event", "sign_up", { method: "La Traversée" });
        }
      } catch (e) {
        /* silencieux */
      }
      if (THANK_YOU_PAGE_URL) {
        setTimeout(() => {
          window.location.href = THANK_YOU_PAGE_URL;
        }, 1200);
      }
    };

    // 1) PRIORITÉ : envoi direct dans Fluent Forms (WordPress, même origine).
    if (!website) {
      try {
        const ok = await submitToFluentForm({ prenom: cleanPrenom, email: cleanEmail });
        if (ok) {
          finishSuccess("fluentform");
          return;
        }
      } catch (ffErr) {
        // Fluent Forms injoignable (ex: aperçu cross-origin) → repli backend ci-dessous
      }
    }

    // 2) REPLI : backend du projet (aperçu de démo, ou si Fluent Forms indisponible)
    try {
      const { data } = await axios.post(`${API}/traversee/register`, {
        prenom: cleanPrenom,
        email: cleanEmail,
        consent,
        website,
        consent_timestamp: new Date().toISOString(),
        ...attribution,
      });

      if (data.status === "success") {
        finishSuccess("backend");
      } else if (data.status === "duplicate") {
        setStatus("duplicate");
        setGlobalError(data.message);
        track("duplicate_contact", { location: formLocation });
      } else {
        setStatus("error");
        if (data.field) setErrors({ [data.field]: data.message });
        else setGlobalError(data.message || "Quelque chose a résisté au courant. Réessaie dans quelques instants.");
        track("form_error", { location: formLocation, reason: data.code || "server" });
      }
    } catch (err) {
      setStatus("error");
      setGlobalError("Quelque chose a résisté au courant. Réessaie dans quelques instants.");
      track("form_error", { location: formLocation, reason: "network" });
    }
  };

  // Mode Fluent Forms : afficher l'emplacement de remplacement (formulaire natif conservé en démo)
  if (FORM_INTEGRATION_MODE === "fluentforms") {
    return <FluentFormsSlot variant={variant} />;
  }

  if (status === "success") {
    return (
      <div ref={rootRef} className={`traversee-${variant}-form lead-card`}>
        <Confirmation testid={testid} />
      </div>
    );
  }

  const isLoading = status === "loading";

  return (
    <div ref={rootRef} className={`traversee-${variant}-form lead-card`} data-testid={`${testid}-card`}>
      <p className="lead-card__eyebrow">INSCRIPTION GRATUITE</p>
      <h3 className="font-display lead-card__title">
        {variant === "hero" ? "Je rejoins La Traversée" : "Prêt à faire le premier pas ?"}
      </h3>
      <p className="lead-card__sub">
        {variant === "hero"
          ? "Reçois immédiatement toutes les informations pour participer aux sept jours d'expérience."
          : "Inscris-toi gratuitement et reçois toutes les informations pour rejoindre La Traversée."}
      </p>

      <CountdownReminder />

      <form onSubmit={submit} noValidate data-testid={`${testid}-element`}>
        {/* Honeypot anti-spam invisible */}
        <div className="hp-field" aria-hidden="true">
          <label htmlFor={`${testid}-website`}>Ne pas remplir</label>
          <input
            id={`${testid}-website`}
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor={`${testid}-prenom`}>Prénom</label>
          <input
            id={`${testid}-prenom`}
            ref={prenomRef}
            type="text"
            autoComplete="given-name"
            value={prenom}
            onFocus={markStarted}
            onChange={(e) => {
              setPrenom(e.target.value);
              if (errors.prenom) setErrors((p) => ({ ...p, prenom: undefined }));
            }}
            aria-invalid={!!errors.prenom}
            aria-describedby={errors.prenom ? `${testid}-prenom-err` : undefined}
            data-testid={`${testid}-prenom-input`}
          />
          {errors.prenom && (
            <p id={`${testid}-prenom-err`} className="field__error" role="alert" data-testid={`${testid}-prenom-error`}>
              {errors.prenom}
            </p>
          )}
        </div>

        <div className="field">
          <label htmlFor={`${testid}-email`}>Adresse email</label>
          <input
            id={`${testid}-email`}
            ref={emailRef}
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onFocus={markStarted}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) setErrors((p) => ({ ...p, email: undefined }));
            }}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? `${testid}-email-err` : undefined}
            data-testid={`${testid}-email-input`}
          />
          {errors.email && (
            <p id={`${testid}-email-err`} className="field__error" role="alert" data-testid={`${testid}-email-error`}>
              {errors.email}
            </p>
          )}
        </div>

        <div className="consent">
          <input
            id={`${testid}-consent`}
            ref={consentRef}
            type="checkbox"
            checked={consent}
            onChange={(e) => {
              setConsent(e.target.checked);
              if (errors.consent) setErrors((p) => ({ ...p, consent: undefined }));
            }}
            aria-invalid={!!errors.consent}
            data-testid={`${testid}-consent-checkbox`}
          />
          <label htmlFor={`${testid}-consent`}>
            J'accepte de recevoir par email les informations nécessaires à La Traversée
            ainsi que les communications de Gilles Delieuze. Je pourrai me désinscrire à
            tout moment.
          </label>
        </div>
        {errors.consent && (
          <p className="field__error" role="alert" data-testid={`${testid}-consent-error`}>
            {errors.consent}
          </p>
        )}

        {globalError && (
          <p className="field__error field__error--global" role="alert" data-testid={`${testid}-global-error`}>
            {globalError}
          </p>
        )}

        {status === "duplicate" && PRIVATE_GROUP_URL && (
          <a
            href={PRIVATE_GROUP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--primary btn--full"
            style={{ marginBottom: "0.9rem" }}
            data-testid={`${testid}-duplicate-group-btn`}
            onClick={() => track("private_group_click", { location: testid, from: "duplicate" })}
          >
            REJOINDRE LE GROUPE PRIVÉ
            <ArrowRight size={18} strokeWidth={1.8} />
          </a>
        )}

        <button
          type="submit"
          className="btn btn--primary btn--full"
          disabled={isLoading}
          data-testid={`${testid}-submit`}
        >
          {isLoading ? (
            <>
              <Loader2 className="spin" size={18} /> INSCRIPTION EN COURS…
            </>
          ) : (
            <>
              {variant === "hero" ? "JE FAIS LE PREMIER PAS" : "JE REJOINS LA TRAVERSÉE"}
              <ArrowRight size={18} strokeWidth={1.8} />
            </>
          )}
        </button>

        <p className="lead-card__micro">
          {variant === "hero"
            ? "Gratuit • Replays inclus • Désinscription possible à tout moment"
            : "Du 23 au 30 septembre 2026 • 3 grandes soirées • Replays inclus"}
        </p>
        {variant === "hero" && (
          <p className="lead-card__note">
            En m'inscrivant, je choisis d'offrir une vraie place à cette expérience pendant
            ces sept jours.
          </p>
        )}
      </form>
    </div>
  );
}
